import { useState, useEffect, useCallback, useRef } from 'react'
import type { League } from '@/services/leagueService'
import { leagueService } from '@/services/leagueService'

interface UseLeaguesResult {
  leagues: League[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useLeagues(groupId: string | undefined): UseLeaguesResult {
  const [leagues, setLeagues] = useState<League[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const mountedRef = useRef(true)
  const requestSeqRef = useRef(0)

  const fetchLeagues = useCallback(async () => {
    if (!groupId) {
      setLeagues([])
      setIsLoading(false)
      return
    }

    const requestSeq = ++requestSeqRef.current
    setIsLoading(true)
    setError(null)

    try {
      const data = await leagueService.list(groupId)
      if (!mountedRef.current || requestSeq !== requestSeqRef.current) return
      setLeagues(data)
    } catch (err) {
      if (!mountedRef.current || requestSeq !== requestSeqRef.current) return
      setError(err instanceof Error ? err.message : 'Failed to load leagues')
    } finally {
      if (!mountedRef.current || requestSeq !== requestSeqRef.current) return
      setIsLoading(false)
    }
  }, [groupId])

  useEffect(() => {
    mountedRef.current = true
    fetchLeagues()
    return () => {
      mountedRef.current = false
    }
  }, [fetchLeagues])

  return {
    leagues,
    isLoading,
    error,
    refetch: fetchLeagues,
  }
}

